import moment from 'moment';
import findIndex from 'lodash/findIndex';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';

import { Action, DropDownOptions, IPreSignedData, PresignedFieldKeys, State } from '../interface';
import { SUPPORTED_FILE_SIZE_AND_FORMAT } from 'shared/constants/constants';
import { FileType } from 'features/creator/interface/creator';
import { DateRange } from 'features/transaction/interface/transaction';
import { PublishedCreatorsType } from 'features/referUrl/interface/referUrlTypes';

export const getEventId = (id: string) => {
	return id.split('::')[0].split('_')[1];
};

export const getChannelId = (id: string) => {
	return id.split('_')[0];
};

export const createAction = (ACTION: string, data: any = null): Action => {
	return {
		type: ACTION,
		payload: data
	};
};

export const createLoadingSelector = (actions: string[]) => (state: State) => {
	// returns true only when all actions is not loading
	let loader = false;
	for (let i = 0; i < actions.length; i += 1) {
		if (state.loading.api[actions[i]]) {
			loader = true;
			break;
		}
	}
	return loader;
};

/**
 * function which returns formatted date
 * @param date
 */
export const formatDate = (date: any, format?: string, isUtc: boolean = false) => {
	if (!date) {
		return '';
	}
	if (!isUtc) {
		return (
			moment(date)
				// .utc()
				// .local()
				.format(format || 'DD-MM-YYYY HH:mm:ss')
		);
	}
	return (
		moment(date)
			.utc()
			// .local()
			.format(format || 'DD-MM-YYYY HH:mm:ss')
	);
};

/**
 * function to convert date to iso string and set seconds/milliseconds to zero
 * @param date
 */
export const convertDateToISOString = (date: any) => {
	if (!date) {
		return '';
	}
	return moment(date).set({ seconds: 0, milliseconds: 0 }).toISOString();
};

export const debounce = (func: any, wait = 400) => {
	let h: NodeJS.Timeout;
	return (...args: any) => {
		clearTimeout(h);
		h = setTimeout(() => func(...args), wait);
	};
};

const numberFormatterInstance = new Intl.NumberFormat('de-DE');

export const numberFormatter = (value: number) => {
	return numberFormatterInstance.format(value);
};

export const setFileInputValue = (
	event: React.ChangeEvent<HTMLInputElement>,
	callBack: (name: string, value: any) => void,
	key: string,
	values: any[] | {},
	types: string[]
) => {
	const files = event.target.files;
	if (!files || !files[0]) {
		return;
	}
	const fileList = Array.from(files);
	let data: any = {};
	if (isArray(values)) {
		data = values;
		if (!data) {
			data = [];
		}
		if (fileList.length > 0 && fileList.length <= 8) {
			for (const file of fileList) {
				const validateFileIndex = types ? findIndex(types, (type) => file.type.startsWith(type)) : -1;
				if (validateFileIndex >= 0) {
					data.push({
						file: file,
						url: window.URL.createObjectURL(file),
						type: file.type.startsWith('image') ? 'image' : 'video'
					});
				}
			}
		}
	} else {
		const file = fileList[0];
		data = {
			file: file,
			url: window.URL.createObjectURL(file),
			type: file.type.startsWith('image') ? 'image' : 'video'
		};
	}
	callBack(key, data);
};

export const checkFileSize = (size: number, file: any): boolean => {
	return file && file.size / 1024 / 1024 <= size;
};

export const checkFilesType = (fileTypes: string[], file: any): boolean => {
	if (!!file) {
		return file && fileTypes.includes(file.type);
	}
	return true;
};

export const isMobile = () => {
	if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') {
		return false;
	}
	return /Mobile/.test(navigator.userAgent);
};

export const windowScroll = (
	value: number,
	position: 'top' | 'bottom' = 'top',
	behavior: 'auto' | 'smooth' = 'smooth'
) =>
	window.scrollTo({
		[position]: value,
		behavior: behavior
	});

export const ensureMediaPermissions = () => {
	return navigator.mediaDevices
		.enumerateDevices()
		.then((devices) => {
			return devices.every((device) => {
				return !(device.deviceId && device.label);
			});
		})
		.then((shouldAskForMediaPermissions) => {
			if (shouldAskForMediaPermissions) {
				return navigator.mediaDevices.getUserMedia({ audio: true, video: true }).then((mediaStream) => {
					mediaStream.getTracks().forEach((track) => {
						track.stop();
					});
				});
			}
		});
};

export const onFileChange = (
	event: any,
	type: string,
	field: string,
	setFieldValue: (field: string, value: any) => void
) => {
	const files = event.target.files;
	onSetFiles(files, type, field, setFieldValue);
};

const onSetFiles = (files: File[], type: string, field: string, setFieldValue: (field: string, value: any) => void) => {
	if (SUPPORTED_FILE_SIZE_AND_FORMAT[type]) {
		checkFilesType(SUPPORTED_FILE_SIZE_AND_FORMAT[type].format, files[0]);
	}
	if (files[0]) {
		const fileName = files[0] ? files[0].name : '';
		const data = {
			file: files[0] || ({} as File),
			name: fileName,
			type: files[0].type,
			url: URL.createObjectURL(files[0])
		};
		setFieldValue(field, data);
	}
};

export const uploadToPreSingedURL = (media: FileType[], preSingedUrls: any[]) => {
	return preSingedUrls.map((item: any, index: number) => {
		let file: any;
		if (media[index].name === item.fileName) {
			file = media[index];
		}
		if (!isEmpty(file)) {
			return new Promise((resolve, reject) => {
				const xhr = new XMLHttpRequest();
				xhr.onload = function () {
					if (this.status === 200) {
						resolve({
							[file.key]: item.cdn,
							isDigitalOcean: true
						});
					}
				};
				xhr.onerror = (err) => {
					reject(err);
				};
				xhr.open('PUT', item.secrets, true);
				xhr.setRequestHeader('Content-Type', file.file.type);
				const blob = new Blob([file.file], { type: file.file.type });
				xhr.send(blob);
			});
		} else {
			return new Promise((resolve, reject) => resolve(''));
		}
	});
};

export const uploadFileToS3PreSigned = (
	presignedPostData: IPreSignedData,
	file: File,
	onProgress?: (val: number) => void,
	key: 'voiceMailUrl' | 'avatarUrl' = 'avatarUrl'
): Promise<string> => {
	return new Promise((resolve, reject) => {
		const formData = new FormData();
		Object.keys(presignedPostData.fields).forEach((key) => {
			formData.append(key, presignedPostData.fields[key as PresignedFieldKeys]);
		});
		formData.append('file', file);
		const xhr = new XMLHttpRequest();
		if (onProgress) {
			xhr.upload.onprogress = (e) => {
				if (e.lengthComputable) {
					const progress = Math.round((e.loaded / file.size) * 100);
					onProgress(progress > 100 ? 100 : progress);
				}
			};
		}
		xhr.open('POST', presignedPostData.uploadUrl, true);
		xhr.send(formData);
		xhr.onload = function () {
			this.status === 204 ? resolve(presignedPostData[key]) : reject(this.responseText);
		};
	});
};

export const isTimeEnded = (time: any): boolean => {
	return moment().toISOString() > time;
};

const formatRecordTime = (record: string) => {
	const convertedRecord = (record || '').toString();
	if (convertedRecord.includes('.')) {
		return convertedRecord;
	}
	return `${record}.0`;
};

export const convertToFormattedRecord = (record: string) => {
	const value = formatRecordTime(record).split('.');
	const seconds = Number(value[0]);
	const milliSeconds = value[1];
	const duration = moment.duration(seconds, 'seconds');
	const fromMinutes = String(Math.floor(duration.asMinutes()));
	const fromSeconds = String(Math.floor(duration.asSeconds() - Number(fromMinutes) * 60));
	return `${(fromMinutes.length === 1 && `0${fromMinutes}`) || fromMinutes}:${
		(fromSeconds.length === 1 && `0${fromSeconds}`) || fromSeconds
	}.${getFormattedMS(milliSeconds)}`;
};

export const getFormattedMS = (ms: string): string => {
	const milliSeconds = ms;
	if (!milliSeconds.length) {
		return '000';
	} else if (milliSeconds.length === 1) {
		return `${milliSeconds}00`;
	} else if (milliSeconds.length === 2) {
		return `${milliSeconds}0`;
	}
	return milliSeconds;
};

export const payoutNumberConverter = (value: number) => {
	const formattedValue = numberFormatter(value);
	const splitValue = formattedValue.split(',');
	if (!splitValue[1] || !splitValue[1].length) {
		return `${formattedValue},00`;
	} else if (splitValue[1].length === 1) {
		return `${splitValue[0]},${splitValue[1]}0`;
	}
	return formattedValue.replace('.', ',');
};

export const convertNumber = (value: number) => {
	// Nine Zeroes for Billions
	return Math.abs(Number(value)) >= 1.0e9
		? Math.abs(Number(value)) / 1.0e9 + 'B'
		: // Six Zeroes for Millions
		Math.abs(Number(value)) >= 1.0e6
		? (Math.abs(Number(value)) / 1.0e6).toFixed(2) + 'M'
		: // Three Zeroes for Thousands
		Math.abs(Number(value)) >= 1.0e3
		? (Math.abs(Number(value)) / 1.0e3).toFixed(2) + 'K'
		: String(Math.abs(Number(value))).includes('.')
		? Math.abs(Number(value)).toFixed(2)
		: Math.abs(Number(value));
};

export const convertNumberWithoutDecimals = (value: number) => {
	// Nine Zeroes for Billions
	return Math.abs(Number(value)) >= 1.0e9
		? Math.abs(Number(value)) / 1.0e9 + 'B'
		: // Six Zeroes for Millions
		Math.abs(Number(value)) >= 1.0e6
		? (Math.abs(Number(value)) / 1.0e6).toFixed(0) + 'M'
		: // Three Zeroes for Thousands
		Math.abs(Number(value)) >= 1.0e3
		? (Math.abs(Number(value)) / 1.0e3).toFixed(0) + 'K'
		: String(Math.abs(Number(value))).includes('.')
		? Math.abs(Number(value)).toFixed(0)
		: Math.abs(Number(value));
};

export const getPayoutNumber = (value: number) => {
	if (value >= 1000) {
		return convertNumber(value);
	}
	return payoutNumberConverter(value);
};

export const selectedOption = (options: DropDownOptions[], value: string | number | string[]) => {
	if (Array.isArray(value)) {
		return options.filter((option) => value.includes(option.value as string));
	}
	return options.filter((option) => option.value === value);
};

export const selectedFilterOption = (options: PublishedCreatorsType[], value: string | number | string[]) => {
	if (Array.isArray(value)) {
		return options.filter((option) => value.includes(option.id as string));
	}
	return options.filter((option) => option.id === value);
};

export const formatDateRange = (dateRange: DateRange, key: string) => {
	let actualStartDate;
	let actualEndDate;
	const currentDate = new Date();
	if (dateRange.startDate) {
		const startDate = dateRange.startDate;
		// tslint:disable-next-line:max-line-length
		actualStartDate = new Date(
			startDate.getFullYear(),
			startDate.getMonth(),
			startDate.getDate(),
			currentDate.getHours(),
			currentDate.getMinutes(),
			currentDate.getSeconds()
		).toISOString();
	}
	if (dateRange.endDate) {
		const endDate = dateRange.endDate;
		// tslint:disable-next-line:max-line-length
		actualEndDate = new Date(
			endDate.getFullYear(),
			endDate.getMonth(),
			endDate.getDate(),
			currentDate.getHours(),
			currentDate.getMinutes(),
			currentDate.getSeconds()
		).toISOString();
	}
	const moments = require('moment-timezone');
	// console.log(new Date(new Date(moments(dateRange.endDate).endOf('day')).setUTCHours(23, 59, 59, 59)).toISOString());
	actualEndDate = new Date(
		new Date(moments(dateRange.endDate).endOf('day')).setUTCHours(23, 59, 59, 59)
	).toISOString() as String;
	actualStartDate = new Date(
		new Date(moments(dateRange.startDate).format('YYYY-MM-DD')).setUTCHours(0, 0, 0, 0)
	).toISOString() as String;
	return { startDate: actualStartDate, endDate: actualEndDate };
};

const numberFormatterInstanceDE = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

export const numberFormatterByCurrency = (value: number) => {
	return numberFormatterInstanceDE.format(value).replace(/[€$]/g, '');
};

export const vaultDateFormat = (ts: string): string => {
	return moment(ts).format('D. MMM');
};

export const trimStringsInObject = (data: { [key: string]: any }) => {
	Object.keys(data).forEach((key) => {
		const value = data[key];
		if (typeof value === 'string') {
			data[key] = value.trim();
		} else if (typeof value === 'object' && value !== null) {
			trimStringsInObject(value);
		}
	});

	return data;
};

/**
 * Start of selected calendar day in UTC (00:00:00.000).
 * Uses the date picker's calendar day (local) so 18-02-2026 → 2026-02-18T00:00:00.000Z.
 */
export function formatStartDate(date: Date): string {
	const y = date.getFullYear();
	const m = date.getMonth();
	const d = date.getDate();
	return new Date(Date.UTC(y, m, d, 0, 0, 0, 0)).toISOString();
}

/**
 * End of selected calendar day in UTC (23:59:59.999).
 * Uses the date picker's calendar day so 01-02-2026 → 2026-02-01T23:59:59.999Z.
 */
export function getApiEndDate(_startDate: Date, endDate: Date): string {
	const y = endDate.getFullYear();
	const m = endDate.getMonth();
	const d = endDate.getDate();
	return new Date(Date.UTC(y, m, d, 23, 59, 59, 999)).toISOString();
}

export function getDefaultDateRange(): DateRange {
	const now = new Date();
	const startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
	const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
	return { startDate, endDate, key: 'selection' };
}

export function getChatDefaultDateRange(): DateRange {
	const now = new Date();
	const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
	const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
	return { startDate, endDate, key: 'selection' };
}

export const formatStatusTimestamp = (value?: string | null): string | null => {
	if (!value) {
		return null;
	}
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return null;
	}
	return parsed.toLocaleString(undefined, {
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	});
};
